/**
 * 流水页 **筛选面板**（10 维 · C 域 PRD §5.1.4 · `AC-C1.13`）
 *
 * ## 形态（2026-10-14 两次订正）
 * | 批次 | 改动 | 依据 |
 * |---|---|---|
 * | 批次 72 | 从「左侧 218px 常驻轨」搬进「吸顶工具条的下拉面板」，成为**全页唯一筛选入口** | `DR-042` · `AC-C1.47` |
 * | 本批（73） | ① 分类由**竖排 Accordion**（实测占 952px / 面板 62%）改为**横排 chip + 尾部箭头展开 L2**；② 分组改**两列网格**（窄屏退回一列）；③ 面板设 `max-height` + 内部滚动 | 负责人：「分类都是竖排，挤占了所有的页面空间」 |
 * | 批次 109 | 维度顺序改为 **账户 → 时间 → 金额区间** 打头（其后分类 / 标签 / 成员 / 方向 / 状态 / 来源），工具条上的搜索框移到最后 | 负责人：「流水的筛选可以选择账户，日期，金额，然后再加最后的搜索框」 |
 *
 * ## 职责边界（🔴 本组件不做任何判定）
 * 它只做两件事：**把选项画出来** + **把点击翻译成草稿变更**。
 * 于是：多维选择规则、选项文案、请求拼装全部在 `features/journal-*.ts`（可直测），
 * 本文件里没有任何"什么算收入""L1 要不要含 L2"之类的口径 ——
 * 「选 L1 自动含其 L2」**在服务端展开**（`AC-C1.14`），端侧只传用户点的 id。
 *
 * ## ⚠️ 单选 / 多选是**维度性质**，不是样式选择
 * | 维度 | 形态 | 依据 |
 * |---|---|---|
 * | 时间（1） | **单选** | 一个窗口就是一段时间 |
 * | 方向（7） | **单选** | 契约写明「用户每次只表达一个意图」（四值可重叠） |
 * | 其余六个清单维度 | 多选 | §5.1.4「维度内 OR」 |
 */

import {
  Box,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreOutlined from '@mui/icons-material/ExpandMoreOutlined';
import { useState, type ReactNode } from 'react';
import type {
  AccountView,
  CategoryView,
  LedgerMemberView,
  TagView,
} from '@jiaying/shared';
import {
  DIRECTION_OPTIONS,
  SOURCE_OPTIONS,
  STATUS_OPTIONS,
  windowOptionsFor,
  type FilterOption,
} from '../features/journal-labels.js';
import {
  JournalWindow,
  type JournalFilterDraft,
} from '../features/journal-filter.js';

export interface JournalFilterRailProps {
  draft: JournalFilterDraft;
  onChange: (next: JournalFilterDraft) => void;
  accounts: readonly AccountView[];
  members: readonly LedgerMemberView[];
  categories: readonly CategoryView[];
  tags: readonly TagView[];
  /** 金额输入的提示（由 `amountInputError()` 算好传进来 —— 本组件不判合法性） */
  amountError: string | null;
  /**
   * 🆕 **用户偏好派生的基线时间档**（`AC-N2.18`）—— 只用来把「默认」标注打在**正确的那一档**上。
   * ⚠️ 本组件**不判**它是哪一档（那是 `windowForPreference()` 的事），只负责转发。
   */
  baseWindow: JournalWindow;
}

/** 在数组里切换一个值（多选维度的唯一实现）—— 泛型保住枚举类型（`string[]` 会丢掉取值域） */
function toggle<T extends string>(list: readonly T[], id: T): T[] {
  return list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
}

/**
 * 一个维度 = 一个「标题 + 内容」格子（两列网格里的单元）。
 *
 * 🔴 为什么用格子而不是原来的「标题一行 + 内容一行」堆叠：宽屏下把 10 个维度**并到两列**，
 * 面板高度直接砍半（实测见 changelog 2026-10-14-b73）。窄屏（`md` 以下）退回单列。
 */
function Field({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Box sx={{ mb: 1.25 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

/** 一簇可切换的选项（单选 / 多选由 `selected` 的语义决定，本组件不判） */
function Facets<T extends string>({
  options,
  selected,
  onToggle,
  onSelect,
}: {
  options: readonly FilterOption<T>[];
  selected: readonly string[];
  onToggle?: (id: T) => void;
  onSelect?: (id: T) => void;
}) {
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
      {options.map((o) => {
        const on = selected.includes(o.id);
        return (
          <Chip
            key={o.id}
            size="small"
            label={o.hint === undefined ? o.label : `${o.label} · ${o.hint}`}
            color={on ? 'primary' : 'default'}
            variant={on ? 'filled' : 'outlined'}
            data-testid={`facet-${o.id}`}
            data-on={on ? 'true' : 'false'}
            onClick={() => {
              if (onSelect !== undefined) onSelect(o.id);
              else onToggle?.(o.id);
            }}
          />
        );
      })}
    </Box>
  );
}

/**
 * 分类维度：两级树（L1 **横排** · 点尾部箭头露出其 L2）—— 「选 L1 含 L2」的展开**在服务端**（`AC-C1.14`）。
 *
 * 🔴 **2026-10-14 形态订正**（负责人：「分类都是竖排，挤占了所有页面空间」）：
 * 原实现给 13 个 L1 **各包一个 `Accordion`**（各自独占一行）⇒ CDP 实测分类区高 **952px**，
 * 占整个面板 1523px 的 **62%**；L1 一路竖排到屏幕外。现改为：
 *  - **L1 横排 chip**（与「账户」同形）—— 13 个 L1 大约占 2 行；
 *  - **主体点击 = 选中 / 取消该 L1**（语义与其它维度一致，不因它是树而特殊）；
 *  - **尾部小箭头 = 只展开 / 收起它的 L2**（🔴 **同时最多开一个** —— 多开等于把竖排问题换个地方复发）；
 *  - 展开的实现用 MUI `Chip` 的 `deleteIcon` + `onDelete`：这是官方「尾部一个可点图标、
 *    且**不冒泡到主体点击**」的机制；自拼两个 chip 拼接会破坏圆角 / 间距的一致性，故不采用。
 *  - ⚠️ 与旧实现的差异：**未展开的 L2 chip 不再进 DOM**（旧 `Accordion` 收起时内容仍挂载）。
 *    已确认无 spec 依赖该行为（`grep facet-cat` 在 client spec 里零命中）。
 */

/**
 * 🆕 批 `112-B`：分类维度**该显示哪些项**（纯函数 —— 交互在静态渲染里点不到，绕 DOM 测是假信号）。
 *
 * 规则两条，第二条的后半段不是贴心而是**诚实**：
 *
 * 1. 未移除的项 ⇒ **恒显示**；
 * 2. 已移除的项 ⇒ 仅当「含已移除」打开，**或它已被选中**时显示。
 *
 * 🔴 为什么"已选中就必须显示"：筛选条件里挂着一个已移除分类、面板却不显示它 ⇒
 * 用户看不到是哪一条，也**取消不了**（只能「清空」全部筛选）。
 * 这是铁律 107 的形态：「对象不在结果里」与「字段为 null」是两种缺失，两种都得能看到。
 */
export function facetVisibleCategories(
  categories: readonly CategoryView[],
  selected: readonly string[],
  showRemoved: boolean,
): CategoryView[] {
  return categories.filter((c) => c.removed !== true || showRemoved || selected.includes(c.id));
}

/** chip 文案：已移除的带后缀（🔴 端侧不改名、不算数，只做**标注**） */
function chipLabel(c: CategoryView): string {
  return c.removed === true ? `${c.name} · 已移除` : c.name;
}

function CategoryFacets({
  categories,
  selected,
  onToggle,
}: {
  categories: readonly CategoryView[];
  selected: readonly string[];
  onToggle: (id: string) => void;
}) {
  const [expanded, setExpanded] = useState<string | null>(null);
  /**
   * 🆕 批 `112-B`：「含已移除」开关（`AC-B3.13` 在**筛选侧**的兑现）。
   *
   * 为什么默认关：分类被移除后本来就不该再出现在日常选择里（`AC-B3.14`）；
   * 但**历史流水仍归属它**（软删，`DR-062` D3），所以要给一条"按历史分类筛"的路 ——
   * 否则用户一移除分类，连它历史上花过的钱都搜不到了。
   *
   * 状态是**组件内**的（不进 `JournalFilterDraft`）：它只影响"面板列出什么"，
   * 不影响"筛什么"，进 draft 会污染查询参数与 URL。
   */
  const [showRemoved, setShowRemoved] = useState(false);
  const removedCount = categories.filter((c) => c.removed === true).length;
  const shown = facetVisibleCategories(categories, selected, showRemoved);
  const l1 = shown.filter((c) => c.level === 1);
  const opened = l1.find((p) => p.id === expanded) ?? null;
  const openedChildren =
    opened === null ? [] : shown.filter((c) => c.parentId === opened.id);

  return (
    <Box data-testid="facet-cat-group">
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {l1.map((parent) => {
          // 🔴 必须用 `shown` 而不是 `categories`：已移除的子类在开关关着时不在列表里，
          // 若拿全量算箭头 ⇒ **箭头在、点开是空的**（假入口）。
          const children = shown.filter((c) => c.parentId === parent.id);
          const on = selected.includes(parent.id);
          const isOpen = expanded === parent.id;
          return (
            <Chip
              key={parent.id}
              size="small"
              label={chipLabel(parent)}
              color={on ? 'primary' : 'default'}
              variant={on ? 'filled' : 'outlined'}
              data-testid={`facet-cat-${parent.id}`}
              data-on={on ? 'true' : 'false'}
              onClick={() => {
                onToggle(parent.id);
              }}
              {...(children.length === 0
                ? {}
                : {
                    // 🔴 尾部箭头：点击**不进** `onClick`（MUI 的 `onDelete` 与主体点击是两个入口）
                    deleteIcon: (
                      <ExpandMoreOutlined
                        sx={{
                          transition: 'transform .15s',
                          transform: isOpen ? 'rotate(180deg)' : 'none',
                        }}
                      />
                    ),
                    onDelete: () => {
                      setExpanded(isOpen ? null : parent.id);
                    },
                  })}
            />
          );
        })}
      </Box>
      {opened !== null && openedChildren.length > 0 ? (
        <Box
          data-testid={`facet-cat-children-${opened.id}`}
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.5,
            mt: 0.75,
            pl: 1,
            borderLeft: 2,
            borderColor: 'divider',
          }}
        >
          {openedChildren.map((child) => {
            const on = selected.includes(child.id);
            return (
              <Chip
                key={child.id}
                size="small"
                label={chipLabel(child)}
                color={on ? 'primary' : 'default'}
                variant={on ? 'filled' : 'outlined'}
                data-testid={`facet-cat-${child.id}`}
                data-on={on ? 'true' : 'false'}
                onClick={() => {
                  onToggle(child.id);
                }}
              />
            );
          })}
        </Box>
      ) : null}
      {/*
        🆕 批 `112-B`：只有**确实存在已移除分类**时才出现这个开关 ——
        一个永远点不出东西的开关比没有更糟（同 110 那次删掉「更多分类」的理由）。
        🔴 `data-testid` 挂在 label 的 span 上：MUI 6 的 `Checkbox.inputProps` 类型不接 `data-*`（铁律 100）。
      */}
      {removedCount > 0 && (
        <FormControlLabel
          data-testid="facet-cat-removed-row"
          sx={{ mt: 0.25, ml: 0, '& .MuiFormControlLabel-label': { fontSize: 12 } }}
          control={
            <Checkbox
              size="small"
              checked={showRemoved}
              onChange={(e) => {
                setShowRemoved(e.target.checked);
              }}
            />
          }
          label={<span data-testid="facet-cat-show-removed">{`含已移除（${String(removedCount)}）`}</span>}
        />
      )}
    </Box>
  );
}

export function JournalFilterRail({
  draft,
  onChange,
  accounts,
  members,
  categories,
  tags,
  amountError,
  baseWindow,
}: JournalFilterRailProps) {
  const patch = (part: Partial<JournalFilterDraft>) => {
    onChange({ ...draft, ...part });
  };

  return (
    <Box data-testid="journal-filter-rail">
      <Stack direction="row" alignItems="center" sx={{ mb: 0.5 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          筛选
        </Typography>
        <Box sx={{ flex: 1 }} />
        {/*
          「清空」= 回到默认态（只开近 3 月 · `AC-C1.17`）。
          🔴 不是"把所有维度设空" —— 那会把默认窗口一起去掉（变成"不限时间"）。
        */}
        <Chip
          size="small"
          variant="outlined"
          label="清空"
          data-testid="filter-clear"
          onClick={() => {
            patch({
              window: JournalWindow.Recent3m,
              from: '',
              to: '',
              accountIds: [],
              memberIds: [],
              categoryIds: [],
              tagIds: [],
              amountMinText: '',
              amountMaxText: '',
              direction: '',
              statuses: [],
              sources: [],
              keyword: '',
            });
          }}
        />
      </Stack>
      <Divider />

      {/* 🔴 2026-10-14 形态订正（负责人：「筛选的分类都是竖排，挤占了所有的页面空间」）：
          原「标题一行 + 内容一行」的**竖排堆叠**改为**两列网格**（`md` 以下退回单列）；
          分类维度另外改成横排 chip —— 详见 `CategoryFacets` 的文件头说明。 */}
      <Box
        data-testid="facet-grid"
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' },
          columnGap: 2.5,
          alignItems: 'start',
          mt: 1,
        }}
      >
        <Field title="账户">
          <Facets
            options={accounts.map((a) => ({ id: a.id, label: a.name }))}
            selected={draft.accountIds}
            onToggle={(id) => {
              patch({ accountIds: toggle(draft.accountIds, id) });
            }}
          />
        </Field>

        <Field title="时间">
          <Facets
            options={windowOptionsFor(baseWindow)}
            selected={[draft.window]}
            onSelect={(id) => {
              patch({ window: id });
            }}
          />
          {draft.window === JournalWindow.Custom && (
            <Stack direction="row" spacing={1} sx={{ mt: 0.75 }}>
              <TextField
                size="small"
                type="date"
                label="起"
                value={draft.from}
                onChange={(e) => {
                  patch({ from: e.target.value });
                }}
                inputProps={{ 'data-testid': 'filter-from' }}
              />
              <TextField
                size="small"
                type="date"
                label="止"
                value={draft.to}
                onChange={(e) => {
                  patch({ to: e.target.value });
                }}
                inputProps={{ 'data-testid': 'filter-to' }}
              />
            </Stack>
          )}
        </Field>

        <Field title="金额区间">
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              size="small"
              placeholder="最小"
              value={draft.amountMinText}
              onChange={(e) => {
                patch({ amountMinText: e.target.value });
              }}
              inputProps={{ 'data-testid': 'filter-amount-min' }}
            />
            <Typography variant="body2" color="text.secondary">
              —
            </Typography>
            <TextField
              size="small"
              placeholder="最大"
              value={draft.amountMaxText}
              onChange={(e) => {
                patch({ amountMaxText: e.target.value });
              }}
              inputProps={{ 'data-testid': 'filter-amount-max' }}
            />
          </Stack>
          {amountError !== null && (
            <Typography variant="caption" color="error" data-testid="filter-amount-error">
              {amountError}
            </Typography>
          )}
        </Field>

        <Field title="分类">
          <CategoryFacets
            categories={categories}
            selected={draft.categoryIds}
            onToggle={(id) => {
              patch({ categoryIds: toggle(draft.categoryIds, id) });
            }}
          />
        </Field>

        <Field title="标签">
          <Facets
            options={tags.map((t) => ({ id: t.id, label: t.name }))}
            selected={draft.tagIds}
            onToggle={(id) => {
              patch({ tagIds: toggle(draft.tagIds, id) });
            }}
          />
        </Field>

        <Field title="成员">
          <Facets
            options={members.map((m) => ({ id: m.userId, label: m.name }))}
            selected={draft.memberIds}
            onToggle={(id) => {
              patch({ memberIds: toggle(draft.memberIds, id) });
            }}
          />
        </Field>

        <Field title="方向">
          <Facets
            options={DIRECTION_OPTIONS}
            selected={draft.direction === '' ? [] : [draft.direction]}
            onSelect={(id) => {
              // 🔴 再点一次 = 取消（单选维度必须能回到"不限"，否则用户被锁死在一个方向上）
              patch({ direction: draft.direction === id ? '' : id });
            }}
          />
        </Field>

        <Field title="状态">
          <Facets
            options={STATUS_OPTIONS}
            selected={draft.statuses}
            onToggle={(id) => {
              patch({ statuses: toggle(draft.statuses, id) });
            }}
          />
        </Field>

        <Field title="来源">
          <Facets
            options={SOURCE_OPTIONS}
            selected={draft.sources}
            onToggle={(id) => {
              patch({ sources: toggle(draft.sources, id) });
            }}
          />
        </Field>
      </Box>
    </Box>
  );
}
